import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { RotateCcw, AlertCircle, BarChart3, FileSpreadsheet } from 'lucide-react';
import DeliveryCsvImportModal from './DeliveryCsvImportModal';
import type { ImportCsvResult, MuMode } from '../hooks/sessionTypes';
import type { CsvParseRow } from '../utils/csvFeatureImport';
import { partitionSessionAttachments } from '../utils/sessionAttachmentRouting';
import WatchModeView from './WatchModeView';
import FlowCanvas from './FlowCanvas';
import SpineView from './SpineView';
import VsmCanvas from './VsmCanvas';
import AddStepToVsmModal from './AddStepToVsmModal';
import FeaturesPanel from './FeaturesPanel';
import DebugPanel from './DebugPanel';
import CommandPalette, { type PaletteItem } from './CommandPalette';
import ImportFromDocumentModal from './ImportFromDocumentModal';
import MermaidImportModal from './MermaidImportModal';
import FlowSourceEditModal from './FlowSourceEditModal';
import { parseFlow } from '../utils/flowParser';
import { getBoundedContexts, computeRelatedBcs } from '../utils/flowBoundedContext';
import { downloadFeatureSpec } from '../utils/featureSpecMarkdown';
import { isBetaEnabled } from '../utils/featureFlags';
import BetaBadge from './BetaBadge';
import ModeToggle, { type ConversationMode } from './ModeToggle';
import CenteredInput from './CenteredInput';
import AppBrand from './AppBrand';
import SessionCapturePanel from './SessionCapturePanel';
import { useSession } from '../hooks/useSession';
import '../styles/ArcPilotShell.css';

// ─── Visual state machine ──────────────────────────────────────────
// `spine` is the narrate state (002-narrate-capture): a session with a
// non-empty flow whose phase is `narrate` shows the forming spine instead
// of the canvas. `with-canvas` is the refine state (and the default for a
// pre-feature session whose phase is absent, FR-015).
type ShellState = 'idle' | 'active' | 'spine' | 'with-canvas';

const ArcPilotShell: React.FC = () => {
  const { sessionId: urlSessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  const [mode, setMode] = useState<ConversationMode>('discovery');
  const [showRestartModal, setShowRestartModal] = useState(false);
  const [debugOpen, setDebugOpen] = useState(false);
  const [deliveryCsvOpen, setDeliveryCsvOpen] = useState(false);
  const [deliveryCsvImporting, setDeliveryCsvImporting] = useState(false);
  const [mermaidImportOpen, setMermaidImportOpen] = useState(false);
  const [flowEditOpen, setFlowEditOpen] = useState(false);

  const session = useSession(urlSessionId, mode);

  const {
    sessionId,
    conversationHistory,
    isLoading,
    dsnModel,
    callStatus,
    handleTextMessage,
    startVoiceCall,
    endVoiceCall,
    createSession,
    downloadTranscript,
    sessionLoading,
    newModel,
    discoveryFeatures,
    isExtractingFeatures,
    refreshDiscoveryFeatures,
    triggerDiscoveryFeatureExtraction,
    importFromDocument,
    importFromMermaid,
    setFlowText,
    isSavingFlowText,
    saveVsm,
    phase,
    spine,
    acceptTransition,
    transitionOffered,
    dismissOffer,
    transitionError,
  } = session;

  const betaOn = isBetaEnabled();

  // Statement id targeted by the "Add to VSM" modal. null = modal closed.
  const [vsmAddStatementId, setVsmAddStatementId] = useState<number | null>(null);

  // Discovery canvas toggle: Flow / VSM / Features.
  const [discoveryView, setDiscoveryView] = useState<'flow' | 'vsm' | 'features'>('flow');
  // Pending statement-focus request from FeaturesPanel "show in flow →".
  // FlowCanvas consumes this on mount/render and calls back to clear it.
  const [pendingFlowFocus, setPendingFlowFocus] = useState<number[] | null>(null);
  const showStatementsInFlow = useCallback((ids: number[]) => {
    setDiscoveryView('flow');
    setPendingFlowFocus(ids);
  }, []);
  // Pending canvas-export request from the palette. Same consume-once
  // pattern as flow focus — FlowCanvas reads it, runs the export, and
  // signals consumption back so we can clear (and surface errors).
  const [pendingFlowExport, setPendingFlowExport] = useState<'svg' | 'png' | null>(null);
  const exportFlowCanvas = useCallback((format: 'svg' | 'png' = 'svg') => {
    setDiscoveryView('flow');
    setPendingFlowExport(format);
  }, []);

  // ─── Command palette (Cmd/Ctrl+K) ────────────────────────────────
  const [paletteOpen, setPaletteOpen] = useState(false);
  // Parsed flow used by both the palette card-search and the spec
  // markdown export. Cheap to recompute when flowText changes.
  const parsedFlowForPalette = useMemo(
    () => parseFlow(newModel?.flowText || ''),
    [newModel?.flowText],
  );

  // ─── Bounded-context filter (shared by canvas + features) ────────
  // Lifting this here so picking a BC in one view drives the other —
  // a customer walkthrough then reads consistently across Flow and
  // Features.
  const [selectedBoundedContext, setSelectedBoundedContext] = useState<string | null>(null);
  const availableBoundedContexts = useMemo(
    () => getBoundedContexts(parsedFlowForPalette),
    [parsedFlowForPalette],
  );
  const relatedBoundedContexts = useMemo(
    () => (selectedBoundedContext
      ? computeRelatedBcs(parsedFlowForPalette, selectedBoundedContext)
      : new Set<string>()),
    [parsedFlowForPalette, selectedBoundedContext],
  );
  // If the selected BC vanishes from the flow (e.g. session switch),
  // clear so we don't render a dangling label.
  useEffect(() => {
    if (selectedBoundedContext && !availableBoundedContexts.find((b) => b.name === selectedBoundedContext)) {
      setSelectedBoundedContext(null);
    }
  }, [selectedBoundedContext, availableBoundedContexts]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const isCmdK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
      if (!isCmdK) return;
      // The palette is available on the landing page too — the only
      // command there is "Start blank session" but it's a useful
      // shortcut for technical users who want to jump straight into
      // a session and set the flow grammar manually.
      e.preventDefault();
      setPaletteOpen((open) => !open);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Spin up an empty session without sending any message — used by the
  // landing-page palette entry "Start blank session" so a technical
  // user can jump straight into the session view and set the flow
  // grammar manually via "Edit flow grammar".
  const handleStartBlankSession = useCallback(async () => {
    const newId = await createSession(mode);
    navigate(`/sessions/${newId}`);
  }, [createSession, mode, navigate]);

  const paletteItems = useMemo<PaletteItem[]>(() => {
    const items: PaletteItem[] = [];
    const close = () => setPaletteOpen(false);
    const hasConversation = conversationHistory.length > 0;
    const hasFeatures = discoveryFeatures.features.length > 0;

    // Landing-page palette — no session yet. Only one useful command:
    // skip the chat opener and jump straight into a session so the
    // operator can edit the flow grammar manually (or run any of the
    // import flows on a fresh blank session).
    if (!sessionId) {
      items.push({
        id: 'cmd:start-blank-session',
        kind: 'command',
        iconChar: '＋',
        label: 'Start blank session',
        hint: 'skip the opener, jump into the canvas',
        keywords: 'start new blank empty session create skip jump open canvas manual',
        run: async () => {
          await handleStartBlankSession();
          close();
        },
      });
      return items;
    }

    // Commands. Disabled commands stay listed but with a muted hint;
    // running them is a no-op so the palette doesn't feel broken when
    // a feature isn't yet available.
    items.push({
      id: 'cmd:download-transcript',
      kind: 'command',
      iconChar: '⬇',
      label: 'Download chat transcript',
      hint: hasConversation ? '.md' : 'no messages yet',
      keywords: 'download transcript chat conversation history markdown export',
      run: () => {
        if (!hasConversation) return;
        downloadTranscript();
        close();
      },
    });
    items.push({
      id: 'cmd:download-spec',
      kind: 'command',
      iconChar: '📄',
      label: 'Download features as spec',
      hint: hasFeatures ? '.md' : 'no features yet',
      keywords: 'download features spec markdown export',
      run: () => {
        if (!hasFeatures) return;
        downloadFeatureSpec(discoveryFeatures, parsedFlowForPalette, {
          sessionTitle: dsnModel?.header?.context || 'Discovery features',
          sessionId: sessionId || undefined,
        });
        close();
      },
    });
    const hasFlow = !!newModel?.flowText?.trim();
    items.push({
      id: 'cmd:export-canvas',
      kind: 'command',
      iconChar: '🖼',
      label: 'Export flow canvas',
      hint: hasFlow ? '.svg' : 'no flow yet',
      keywords: 'export canvas flow svg image diagram download',
      run: () => {
        if (!hasFlow) return;
        exportFlowCanvas('svg');
        close();
      },
    });
    items.push({
      id: 'cmd:import-mermaid',
      kind: 'command',
      iconChar: '🧜',
      label: 'Import from Mermaid',
      hint: sessionId ? 'sequenceDiagram → flow' : 'no session yet',
      keywords: 'import mermaid sequence diagram convert paste flow',
      run: () => {
        if (!sessionId) return;
        setMermaidImportOpen(true);
        close();
      },
    });
    items.push({
      id: 'cmd:edit-flow-source',
      kind: 'command',
      iconChar: '✎',
      label: 'Edit flow grammar',
      hint: sessionId
        ? (hasFlow ? `${(newModel?.flowText.match(/^\s*\[\d+\]/gm) || []).length} statements` : 'empty flow')
        : 'no session yet',
      keywords: 'edit flow grammar source dsl manual statement text raw',
      run: () => {
        if (!sessionId) return;
        setFlowEditOpen(true);
        close();
      },
    });
    // Cards — every flow statement is a search target. Switching to
    // the Flow tab + focusing on it is the same flow as FeaturesPanel
    // "show in flow →".
    for (const s of parsedFlowForPalette.statements) {
      items.push({
        id: `card:${s.id}`,
        kind: 'card',
        swatch: s.type,
        iconChar: cardIconChar(s.type),
        label: s.name || `(unnamed ${s.type})`,
        hint: `[${s.id}] ${s.type}${s.target ? ` · &${s.target}` : ''}`,
        keywords: [
          s.type,
          s.target || '',
          s.actor?.name || '',
          ...(s.via?.sys || []),
          Object.keys(s.tags || {}).map((k) => `#${k}`).join(' '),
        ].join(' '),
        run: () => {
          showStatementsInFlow([s.id]);
          close();
        },
      });
    }

    return items;
  }, [
    parsedFlowForPalette,
    conversationHistory.length,
    discoveryFeatures,
    dsnModel?.header?.context,
    sessionId,
    newModel?.flowText,
    downloadTranscript,
    showStatementsInFlow,
    exportFlowCanvas,
    handleStartBlankSession,
  ]);

  // ─── Shell state derivation ──────────────────────────────────────
  // PHASE-authoritative (002-narrate-capture, FR-006/FR-015). The
  // sessionId check comes FIRST: a non-empty flowText must not force a
  // canvas when there is no session. With a session and a non-empty flow,
  // phase decides — `narrate` ⇒ the forming spine; `refine` (or an absent
  // phase, for pre-feature sessions) ⇒ today's canvas (FR-015 default).
  const hasFlow = !!newModel?.flowText && newModel.flowText.trim().length > 0;
  const shellState: ShellState = useMemo(() => {
    if (!sessionId) return 'idle';
    if (!hasFlow) return 'active';
    return phase === 'narrate' ? 'spine' : 'with-canvas';
  }, [sessionId, hasFlow, phase]);

  // Queue of structured docs waiting for the import modal. Processed
  // one at a time — each import rewrites the flow, so parallel runs
  // would race. The modal renders the head of the queue; on close it
  // shifts to the next.
  const [docImportQueue, setDocImportQueue] = useState<{ text: string; filename: string }[]>([]);
  const queueDocImports = useCallback((docs: { file: File; text: string }[]) => {
    if (docs.length === 0) return;
    setDocImportQueue((prev) => [
      ...prev,
      ...docs.map((d) => ({ text: d.text, filename: d.file.name })),
    ]);
  }, []);
  const advanceDocImportQueue = useCallback(() => {
    setDocImportQueue((prev) => prev.slice(1));
  }, []);
  const currentDocImport = docImportQueue[0] ?? null;

  // ─── Session creation for first message ──────────────────────────
  const handleFirstMessage = useCallback(
    async (message: string, files?: File[]) => {
      const all = files ?? [];
      const { structured, regular } = await partitionSessionAttachments(all);
      const newId = await createSession(mode);
      window.history.replaceState(null, '', `/sessions/${newId}`);
      // Queue any structured docs — the modal renders them one at a
      // time with the busy spinner / conflict confirm UI.
      if (structured.length > 0) queueDocImports(structured);
      // Always send the chat message + any non-structured attachments.
      // If there's no message and no regular files, skip the chat call.
      if (message || regular.length > 0) {
        handleTextMessage(message, regular);
      }
    },
    [createSession, mode, handleTextMessage, queueDocImports],
  );

  const handleStartVoice = useCallback(async () => {
    const newId = await createSession(mode);
    window.history.replaceState(null, '', `/sessions/${newId}`);
    startVoiceCall();
  }, [createSession, mode, startVoiceCall]);

  // ─── Restart session ─────────────────────────────────────────────
  const handleRestart = useCallback(() => {
    setShowRestartModal(false);
    window.location.href = '/';
  }, []);

  // ─── Landing CSV → delivery-mode session ──────────────────────────
  // Bootstraps a brand-new session in delivery mode, imports the CSV
  // (with the wizard's chosen MU mode), and navigates straight to the
  // Delivery view. The chat shell never opens for this entry path.
  const handleLandingCsvImport = useCallback(
    async (
      rows: CsvParseRow[],
      opts: { muMode: MuMode; force?: boolean; singleMuName?: string },
    ): Promise<ImportCsvResult | null> => {
      setDeliveryCsvImporting(true);
      try {
        const created = await fetch('/api/sessions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mode: 'delivery' }),
        });
        if (!created.ok) throw new Error(`Session create failed (HTTP ${created.status})`);
        const session = await created.json();
        const newId: string = session.id;

        const importRes = await fetch(`/api/sessions/${newId}/delivery-analysis/import-csv`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            rows,
            muMode: opts.muMode,
            force: !!opts.force,
            singleMuName: opts.singleMuName,
          }),
        });
        if (!importRes.ok) {
          const err = await importRes.json().catch(() => ({}));
          throw new Error(err?.error || `Import failed (HTTP ${importRes.status})`);
        }
        const data = await importRes.json();
        // Navigate to the new Delivery view. The page mount loads the
        // analysis blob via useDeliveryAnalysis.
        navigate(`/sessions/${newId}/delivery`);
        return {
          imported: data.imported || 0,
          duplicates: data.duplicates || [],
          rejected: data.rejected || [],
          payload: data,
        };
      } catch (err) {
        console.error('[Landing CSV] failed:', err);
        return null;
      } finally {
        setDeliveryCsvImporting(false);
      }
    },
    [navigate],
  );

  // ─── Loading screen ──────────────────────────────────────────────
  if (urlSessionId && sessionLoading) {
    return (
      <div className="ap-shell" data-state="idle">
        <div className="ap-loading">Loading session...</div>
      </div>
    );
  }

  return (
    <div className="ap-shell" data-state={shellState}>
      {/* Header */}
      <header className="ap-header">
        <AppBrand />

        {/* Show compact mode badge in header only when session is active */}
        {shellState !== 'idle' && (
          <ModeToggle mode={mode} onChange={setMode} disabled compact />
        )}

        <div className="ap-header-actions">
          {sessionId && <span className="ap-session-id">{sessionId}</span>}
          {sessionId && (
            <button
              className="ap-header-btn"
              onClick={() => setShowRestartModal(true)}
              type="button"
              title="Start a new session"
            >
              <RotateCcw size={14} />
              Start Over
            </button>
          )}
          {sessionId && mode === 'discovery' && betaOn && (
            <Link
              to={`/sessions/${sessionId}/delivery`}
              className="ap-header-btn"
              title="Open the Delivery view — group features and plan ship order"
            >
              <BarChart3 size={14} />
              Delivery view
            </Link>
          )}
        </div>
      </header>

      {/* Main content area */}
      <main className="ap-main">
        {/* Centered input (visible when idle) */}
        {shellState === 'idle' && (
          <>
            <CenteredInput
              mode={mode}
              onModeChange={setMode}
              onSubmit={handleFirstMessage}
              onStartVoice={handleStartVoice}
            />
            {betaOn && (
              <div className="ap-idle-secondary">
                <span className="ap-idle-or">or</span>
                <button
                  type="button"
                  className="ap-idle-import-btn"
                  onClick={() => setDeliveryCsvOpen(true)}
                  title="Start a delivery session by importing a CSV of features"
                >
                  <FileSpreadsheet size={14} />
                  <span>Start with CSV</span>
                  <BetaBadge />
                </button>
              </div>
            )}
          </>
        )}

        {/* Watch mode panel */}
        {shellState !== 'idle' && mode === 'watch' && (
          <div className="ap-chat-panel" data-state={shellState}>
            <WatchModeView
              isListening={callStatus === 'On Call'}
              conversationHistory={conversationHistory}
              onStartListening={startVoiceCall}
              onStopListening={endVoiceCall}
              isSyncing={isLoading}
            />
          </div>
        )}

        {/* Chat panel (Discovery / Infer modes) */}
        {shellState !== 'idle' && mode !== 'watch' && (
          <SessionCapturePanel
            session={session.chat}
            dataState={shellState}
            dataSpineRail={shellState === 'spine'}
            dataWithDebug={mode === 'discovery' && debugOpen}
          />
        )}

        {/* Spine panel (visible while narrating). Shows the forming spine
            in the canvas region in place of FlowCanvas, plus the non-modal
            transition affordance (FR-006/FR-010/FR-011/FR-012). Two shapes:
              - When the agent is OFFERING (transitionOffered, turnKind
                'offer'): a non-modal two-choice offer near the spine — a
                coral PRIMARY ("walk through it on the map") that accepts the
                flip, and a SECONDARY ("I've got more to add") that dismisses
                the offer and keeps the SME narrating (§6.2). Escape on the
                offer dismisses (never accepts); focus is NOT stolen from the
                composer.
              - Otherwise: the always-available manual flip (FR-012) — a
                single primary action, no decline.
            The composer stays enabled beneath either shape so the SME can
            keep narrating (FR-010). A failed flip surfaces transitionError
            (P1-review nit). */}
        {shellState === 'spine' && (
          <div className="ap-canvas-panel ap-spine-panel">
            <SpineView steps={spine} isListening={callStatus === 'On Call'} />
            {transitionError && (
              <div className="ap-spine-transition-error" role="alert">
                <AlertCircle size={14} aria-hidden="true" />
                <span>{transitionError}</span>
              </div>
            )}
            <div
              className="ap-spine-transition"
              onKeyDown={(e) => {
                // Escape on the offer = "keep talking" → dismiss, never
                // accept. Only meaningful while an offer is up.
                if (e.key === 'Escape' && transitionOffered) {
                  e.preventDefault();
                  dismissOffer();
                }
              }}
            >
              {transitionOffered ? (
                <>
                  <button
                    type="button"
                    className="ap-spine-transition-btn ap-spine-transition-btn--primary"
                    onClick={() => { void acceptTransition(); }}
                  >
                    Let&apos;s walk through it on the map
                  </button>
                  <button
                    type="button"
                    className="ap-spine-transition-btn ap-spine-transition-btn--decline"
                    onClick={() => { dismissOffer(); }}
                  >
                    I&apos;ve got more to add
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="ap-spine-transition-btn ap-spine-transition-btn--primary"
                  onClick={() => { void acceptTransition(); }}
                >
                  Let&apos;s go through it on the map
                </button>
              )}
            </div>
          </div>
        )}

        {/* Canvas panel (visible when with-canvas) */}
        {shellState === 'with-canvas' && (
          <div className="ap-canvas-panel">
            {newModel && (
              <>
                <div className="ap-canvas-toggle">
                  <button
                    type="button"
                    className={`ap-canvas-toggle-tab ${discoveryView === 'flow' ? 'ap-canvas-toggle-tab--active' : ''}`}
                    onClick={() => setDiscoveryView('flow')}
                  >
                    Flow
                  </button>
                  {betaOn && (
                    <button
                      type="button"
                      className={`ap-canvas-toggle-tab ${discoveryView === 'vsm' ? 'ap-canvas-toggle-tab--active' : ''}`}
                      onClick={() => setDiscoveryView('vsm')}
                      disabled={!(newModel.vsm?.processes && newModel.vsm.processes.length > 0)}
                      title={
                        newModel.vsm?.processes && newModel.vsm.processes.length > 0
                          ? 'Value Stream Map for the captured process'
                          : 'No timing captured yet — tell the facilitator how long the steps take'
                      }
                    >
                      VSM<BetaBadge />
                      {newModel.vsm?.processes && newModel.vsm.processes.length > 0 && (
                        <span className="ap-canvas-toggle-count">{newModel.vsm.processes.length}</span>
                      )}
                    </button>
                  )}
                  <button
                    type="button"
                    className={`ap-canvas-toggle-tab ${discoveryView === 'features' ? 'ap-canvas-toggle-tab--active' : ''}`}
                    onClick={() => {
                      setDiscoveryView('features');
                      // Pull the latest features when the user switches in.
                      refreshDiscoveryFeatures();
                    }}
                  >
                    Features
                    {discoveryFeatures.features.length > 0 && (
                      <span className="ap-canvas-toggle-count">{discoveryFeatures.features.length}</span>
                    )}
                  </button>
                </div>
                <div className="ap-canvas-pane-host">
                  {betaOn && discoveryView === 'vsm' && (
                    <VsmCanvas newModel={newModel} onSaveVsm={saveVsm} />
                  )}
                  {discoveryView === 'flow' && (
                    <FlowCanvas
                      newModel={newModel}
                      pendingFocus={pendingFlowFocus}
                      onPendingFocusConsumed={() => setPendingFlowFocus(null)}
                      pendingExport={pendingFlowExport}
                      onPendingExportConsumed={(err) => {
                        setPendingFlowExport(null);
                        if (err) console.error('[FlowExport] failed:', err);
                      }}
                      selectedBoundedContext={selectedBoundedContext}
                      onBoundedContextChange={setSelectedBoundedContext}
                      onAddToVsm={(statementId) => setVsmAddStatementId(statementId)}
                    />
                  )}
                  {discoveryView === 'features' && (
                    <FeaturesPanel
                      payload={discoveryFeatures}
                      isExtracting={isExtractingFeatures}
                      hasFlow={!!newModel.flowText.trim()}
                      currentStatementCount={(newModel.flowText.match(/^\s*\[\d+\]/gm) || []).length}
                      onRefresh={refreshDiscoveryFeatures}
                      onReExtract={triggerDiscoveryFeatureExtraction}
                      onShowStatementsInFlow={showStatementsInFlow}
                      selectedBoundedContext={selectedBoundedContext}
                      relatedBoundedContexts={relatedBoundedContexts}
                      availableBoundedContexts={availableBoundedContexts}
                      onBoundedContextChange={setSelectedBoundedContext}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Debug panel (Discovery mode, right edge of the shell). Lives
            after the canvas in DOM order so flex puts it on the right;
            collapsed by default — click the {} rail to open. */}
        {shellState !== 'idle' && mode === 'discovery' && newModel && (
          <DebugPanel
            newModel={newModel}
            conversationHistory={conversationHistory}
            isOpen={debugOpen}
            onToggle={() => setDebugOpen(!debugOpen)}
          />
        )}
      </main>

      {/* Command palette (Cmd/Ctrl+K) */}
      <CommandPalette
        isOpen={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        items={paletteItems}
      />

      {/* Landing CSV → delivery-mode session creator. Visible only when idle. */}
      {betaOn && (
        <DeliveryCsvImportModal
          isOpen={deliveryCsvOpen}
          onClose={() => setDeliveryCsvOpen(false)}
          onImport={handleLandingCsvImport}
          importing={deliveryCsvImporting}
          title="Start with CSV — create a delivery session"
        />
      )}

      {/* Doc-import modal — driven by the auto-route queue. Mounted in
          'into-session' mode because every queued import targets the
          (already-existing) current session. The modal handles its own
          busy state, the destructive-overwrite confirm, and the error
          surface; we just shift the next item off when it closes. */}
      <ImportFromDocumentModal
        mode="into-session"
        isOpen={currentDocImport !== null}
        onClose={advanceDocImportQueue}
        presetContent={currentDocImport ?? undefined}
        onSubmit={async (text, { filename, force }) =>
          importFromDocument(text, { filename, force })
        }
      />

      <MermaidImportModal
        isOpen={mermaidImportOpen}
        onClose={() => setMermaidImportOpen(false)}
        sessionId={sessionId ?? null}
        onImport={importFromMermaid}
      />

      <FlowSourceEditModal
        isOpen={flowEditOpen}
        onClose={() => setFlowEditOpen(false)}
        sessionId={sessionId ?? null}
        currentFlowText={newModel?.flowText || ''}
        onSave={setFlowText}
        isSaving={isSavingFlowText}
      />

      <AddStepToVsmModal
        isOpen={vsmAddStatementId !== null}
        onClose={() => setVsmAddStatementId(null)}
        statementId={vsmAddStatementId}
        statementLabel={
          vsmAddStatementId == null
            ? ''
            : (parsedFlowForPalette.statements.find((s) => s.id === vsmAddStatementId)?.name || '')
        }
        vsm={newModel?.vsm ?? { processes: [] }}
        onSave={saveVsm}
      />

      {/* Restart Modal */}
      {showRestartModal && (
        <div className="ap-modal-backdrop" onClick={() => setShowRestartModal(false)}>
          <div className="ap-modal ap-restart-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ap-restart-modal-icon">
              <RotateCcw size={28} />
            </div>
            <h3>Start Over?</h3>
            <p>
              This will take you back to the home screen where you can choose a new mode and start fresh.
              Your current session will remain saved.
            </p>
            <div className="ap-modal-actions">
              <button className="ap-modal-cancel" onClick={() => setShowRestartModal(false)}>
                Cancel
              </button>
              <button className="ap-modal-confirm" onClick={handleRestart}>
                Start Over
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ArcPilotShell;

// Single-char icon for a palette card row, by statement type. Kept
// here rather than in CommandPalette so the palette stays generic.
function cardIconChar(type: string): string {
  if (type === 'action') return '▶';
  if (type === 'query') return '?';
  if (type === 'command') return '!';
  if (type === 'event') return '◆';
  if (type === 'reaction') return '⚡';
  return '◇';
}
